import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { buildTheme } from "@/theme";
import type { ColorMode } from "@/theme";

interface ColorModeState {
  mode: ColorMode;
  toggle: () => void;
}

const ColorModeContext = createContext<ColorModeState>({ mode: "light", toggle: () => {} });

const KEY = "mdc_theme_mode";

function initial(): ColorMode {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    // Private windows and locked-down browsers throw on storage access; the
    // Console still has to render, it just forgets the preference.
    return "light";
  }
}

/** Light/dark, remembered per browser — the same choice MultiDoc ECM offers. */
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ColorMode>(initial);

  const value = useMemo<ColorModeState>(
    () => ({
      mode,
      toggle: () =>
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          try {
            localStorage.setItem(KEY, next);
          } catch {
            /* preference is a convenience, not state we depend on */
          }
          return next;
        }),
    }),
    [mode],
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeState {
  return useContext(ColorModeContext);
}
